import { createContext, useContext } from 'react';

import type { AccountLinkPort } from '../../features/auth/application/ports/accountLinkPort';
import type { AuthPort } from '../../features/auth/application/ports/authPort';
import type { FundPolicyPort } from '../../features/auth/application/ports/fundPolicyPort';
import type { OrganisationPort } from '../../features/auth/application/ports/organisationPort';
import type { DependentPort } from '../../features/employees/application/ports/dependentPort';
import type { EmployeePort } from '../../features/employees/application/ports/employeePort';
import type { DeclarationPort } from '../../features/declarations/application/ports/declarationPort';
import type { DocumentPort } from '../../features/documents/application/ports/documentPort';
import type { ProgressPort } from '../../features/committee/application/ports/progressPort';
import type { EmployeeAdminPort } from '../../features/committee/application/ports/employeeAdminPort';
import type { RegistrationReviewPort } from '../../features/committee/application/ports/registrationReviewPort';
import type { RolesPort } from '../../features/committee/application/ports/rolesPort';
import type { SettingsPort } from '../../features/settings/application/ports/settingsPort';

/**
 * The ports the application runs on.
 *
 * Kept in its own module because a file that exports both a component and a
 * hook breaks fast refresh — the whole module reloads and component state is
 * lost on every edit. Splitting it is the fix the linter is pointing at.
 */
export interface Services {
  readonly auth: AuthPort;
  readonly links: AccountLinkPort;
  readonly organisation: OrganisationPort;
  readonly employees: EmployeePort;
  readonly dependents: DependentPort;
  readonly fundPolicy: FundPolicyPort;
  readonly declarations: DeclarationPort;
  readonly documents: DocumentPort;
  readonly progress: ProgressPort;
  readonly settings: SettingsPort;
  readonly registrationReview: RegistrationReviewPort;
  readonly employeeAdmin: EmployeeAdminPort;
  readonly roles: RolesPort;
}

export const ServicesContext = createContext<Services | null>(null);

export function useServices(): Services {
  const services = useContext(ServicesContext);
  if (!services) {
    throw new Error('useServices must be used inside <AppProviders>');
  }
  return services;
}
